import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Settings, ChevronDown } from "lucide-react";
import API from "../api/api";
import { HaulMonitorLogo } from "../brand/HaulMonitorLogo";

const POLL_INTERVAL_MS = 60_000;

function NavBadge({ count }) {
  if (!count || count < 1) return null;
  return (
    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function Navbar() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [notifications, setNotifications] = useState({ loads: { total: 0 }, shipments: { total: 0 } });
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const settingsRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await API.get('notifications/count/');
      setNotifications(res.data);
    } catch {
      // Silently ignore — badge simply won't update
    }
    try {
      const res = await API.get('agent/actions/?status=pending_approval');
      const data = res.data.results || res.data;
      setPendingApprovals(Array.isArray(data) ? data.length : 0);
    } catch {
      // Silently ignore — badge simply won't update
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setIsSettingsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('tokenExpiry');
    navigate('/login');
  };

  const navLinkClass = "flex items-center text-gray-300 hover:bg-[#0E1726] hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150";
  const mobileNavLinkClass = "flex items-center text-gray-300 hover:bg-[#0E1726] hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors duration-150";

  return (
    <nav className="bg-[#152744]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <HaulMonitorLogo size={28} variant="lockup" color="#ffffff" title="Haul Monitor" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-[#0E1726] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors duration-150"
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-1">
              {/* Shipment changes / cancellations are flagged on the dashboard grid rows */}
              <Link to="/dashboard" className={navLinkClass}>
                Dashboard
                <NavBadge count={notifications.shipments?.total} />
              </Link>
              <Link to="/tenders" className={navLinkClass}>
                Tenders
                <NavBadge count={notifications.loads?.total} />
              </Link>
              <Link to="/carriers" className={navLinkClass}>
                Carriers
              </Link>
              <Link to="/invoices" className={navLinkClass}>
                Invoices
              </Link>
              <Link to="/agent-approvals" className={navLinkClass}>
                Approvals
                <NavBadge count={pendingApprovals} />
              </Link>

              {/* Settings dropdown */}
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setIsSettingsOpen((v) => !v)}
                  className={`${navLinkClass} cursor-pointer gap-1`}
                >
                  <Settings size={15} />
                  Settings
                  <ChevronDown size={13} className={`transition-transform duration-150 ${isSettingsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isSettingsOpen && (
                  <div className="absolute right-0 mt-1 w-44 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <Link
                        to="/settings/account"
                        onClick={() => setIsSettingsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Account
                      </Link>
                      <Link
                        to="/settings/drivers"
                        onClick={() => setIsSettingsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Drivers
                      </Link>
                      <Link
                        to="/settings/quickbooks"
                        onClick={() => setIsSettingsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        QuickBooks
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleLogout}
                className={`${navLinkClass} cursor-pointer`}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden border-t border-[#0E1726]`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <Link to="/dashboard" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
            Dashboard
            <NavBadge count={notifications.shipments?.total} />
          </Link>
          <Link to="/tenders" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
            Tenders
            <NavBadge count={notifications.loads?.total} />
          </Link>
          <Link to="/carriers" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
            Carriers
          </Link>
          <Link to="/invoices" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
            Invoices
          </Link>
          <Link to="/agent-approvals" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
            Approvals
            <NavBadge count={pendingApprovals} />
          </Link>
          <Link
            to="/settings/account"
            className={mobileNavLinkClass}
            onClick={() => setIsMenuOpen(false)}
          >
            Account Settings
          </Link>
          <Link
            to="/settings/drivers"
            className={mobileNavLinkClass}
            onClick={() => setIsMenuOpen(false)}
          >
            Drivers
          </Link>
          <Link
            to="/settings/quickbooks"
            className={mobileNavLinkClass}
            onClick={() => setIsMenuOpen(false)}
          >
            QuickBooks
          </Link>
          <button
            onClick={handleLogout}
            className={`${mobileNavLinkClass} w-full text-left cursor-pointer`}
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
